import {
  IonAlert,
  IonButton,
  IonButtons,
  IonCheckbox,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonLabel,
  IonModal,
  IonSegment,
  IonSegmentButton,
  IonSpinner,
  IonTitle,
  IonToolbar,
  useIonActionSheet,
} from "@ionic/react";
import { callOutline, createOutline, navigateOutline } from "ionicons/icons";
import { useEffect, useMemo, useState } from "react";
import PlayPauseButton from "./PlayPauseButton.jsx";
import SearchSelect from "./SearchSelect.jsx";
import StatusBadge from "./StatusBadge.jsx";
import TimerDisplay from "./TimerDisplay.jsx";
import { assignedEmployeeCount, assignedEmployees } from "../utils/assignments.js";
import { formatDate, formatDuration, formatTime } from "../utils/date.js";
import {
  calculatedScheduledEnd,
  durationPartsFromMinutes,
  estimatedMinutesFromParts,
  normalizeMinutePart,
} from "../utils/jobDuration.js";

function datetimeLocalValue(value) {
  const date = new Date(value);

  if (!Number.isFinite(date.getTime())) {
    return "";
  }

  const timezoneOffsetMs = date.getTimezoneOffset() * 60000;

  return new Date(date.getTime() - timezoneOffsetMs).toISOString().slice(0, 16);
}

function jobLabel(job) {
  return job?.internalLabel ? `${job.client?.name} - ${job.internalLabel}` : job?.client?.name;
}

function elapsedEntrySeconds(entry) {
  const base = Number(entry?.durationSeconds || 0);

  if (!entry?.isRunning) {
    return base;
  }

  const startTime = new Date(entry.startTime);

  if (!Number.isFinite(startTime.getTime())) {
    return base;
  }

  return base + Math.max(0, Math.floor((Date.now() - startTime.getTime()) / 1000));
}

function durationParts(seconds) {
  const safeSeconds = Math.max(0, Math.round(Number(seconds) || 0));

  return {
    hours: String(Math.floor(safeSeconds / 3600)),
    minutes: String(Math.floor((safeSeconds % 3600) / 60)).padStart(2, "0"),
  };
}

function durationSeconds(parts) {
  const hours = Math.max(0, Number.parseInt(parts?.hours || "0", 10) || 0);
  const minutes = Math.min(59, Math.max(0, Number.parseInt(parts?.minutes || "0", 10) || 0));

  return hours * 3600 + minutes * 60;
}

function effectivePayrollSeconds(job, userId) {
  const entries = job?.timeEntries || [];
  const adjustedEntries = entries.filter(
    (entry) => entry.userId === userId && entry.source?.name === "AdminAdjusted",
  );

  if (adjustedEntries.length > 0) {
    return adjustedEntries.reduce((sum, entry) => sum + elapsedEntrySeconds(entry), 0);
  }

  const manualEntries = entries.filter((entry) => entry.source?.name === "Manual");

  if (manualEntries.length > 0) {
    return manualEntries.reduce((sum, entry) => sum + elapsedEntrySeconds(entry), 0);
  }

  const estimatedEntries = entries.filter(
    (entry) => entry.userId === userId && entry.source?.name === "Estimated",
  );

  if (estimatedEntries.length > 0) {
    return estimatedEntries.reduce((sum, entry) => sum + elapsedEntrySeconds(entry), 0);
  }

  return Math.max(0, Math.round(
    ((job?.estimatedDuration || 0) * 60) / Math.max(1, assignedEmployeeCount(job)),
  ));
}

function effectiveHourlyRate(job, user) {
  const assignment = job?.assignments?.find((candidate) => candidate.userId === user.id);

  return Number(assignment?.hourlyRateOverride ?? user.hourlyRate ?? 0);
}

export default function ExpandedJobCard({
  job,
  currentUserId,
  isOpen,
  canEditSchedule = false,
  users = [],
  onClose,
  onToggleTimer,
  onUpdateSchedule,
  onCancelSchedule,
  onDeleteSchedule,
  isDeletingSchedule = false,
}) {
  const [present] = useIonActionSheet();
  const [isEditingSchedule, setIsEditingSchedule] = useState(false);
  const [isSavingSchedule, setIsSavingSchedule] = useState(false);
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isTimeAllocationEnabled, setIsTimeAllocationEnabled] = useState(false);
  const [isPayAllocationEnabled, setIsPayAllocationEnabled] = useState(false);
  const [allocationMode, setAllocationMode] = useState("same");
  const [sameAllocation, setSameAllocation] = useState({ hours: "0", minutes: "00" });
  const [employeeAllocations, setEmployeeAllocations] = useState({});
  const [payAllocationMode, setPayAllocationMode] = useState("same");
  const [samePayRate, setSamePayRate] = useState("0");
  const [employeePayRates, setEmployeePayRates] = useState({});
  const [scheduleForm, setScheduleForm] = useState({
    scheduledStart: "",
    estimatedDurationHours: "",
    estimatedDurationMinutesPart: "00",
    assignedUserIds: [],
  });
  const scheduledDurationMinutes = useMemo(
    () => estimatedMinutesFromParts(scheduleForm.estimatedDurationHours, scheduleForm.estimatedDurationMinutesPart),
    [scheduleForm.estimatedDurationHours, scheduleForm.estimatedDurationMinutesPart],
  );
  const standardAssignedUsers = useMemo(() => {
    const selectedIds = new Set(scheduleForm.assignedUserIds);

    return users.filter(
      (user) => selectedIds.has(user.id) && user.role?.name === "standard_user" && !user.isOwner,
    );
  }, [scheduleForm.assignedUserIds, users]);

  useEffect(() => {
    if (job) {
      const assignedUserIds = job.assignments?.map((assignment) => assignment.userId) || [];
      const standardUsers = users.filter(
        (user) => assignedUserIds.includes(user.id) && user.role?.name === "standard_user" && !user.isOwner,
      );
      const nextAllocations = Object.fromEntries(
        standardUsers.map((user) => [user.id, durationParts(effectivePayrollSeconds(job, user.id))]),
      );
      const nextPayRates = Object.fromEntries(
        standardUsers.map((user) => [user.id, String(effectiveHourlyRate(job, user))]),
      );
      const uniquePayRates = new Set(Object.values(nextPayRates));
      const estimatedDurationParts = durationPartsFromMinutes(job.estimatedDuration);

      setScheduleForm({
        scheduledStart: datetimeLocalValue(job.scheduledStart),
        estimatedDurationHours: estimatedDurationParts.hours,
        estimatedDurationMinutesPart: estimatedDurationParts.minutes,
        assignedUserIds,
      });
      setAllocationMode("same");
      setEmployeeAllocations(nextAllocations);
      setSameAllocation(nextAllocations[standardUsers[0]?.id] || { hours: "0", minutes: "00" });
      setPayAllocationMode(uniquePayRates.size <= 1 ? "same" : "employee");
      setEmployeePayRates(nextPayRates);
      setSamePayRate(nextPayRates[standardUsers[0]?.id] || "0");
      setIsEditingSchedule(false);
      setIsCancelOpen(false);
      setIsDeleteOpen(false);
      setIsTimeAllocationEnabled(false);
      setIsPayAllocationEnabled(false);
    }
  }, [job, users]);

  if (!job) {
    return null;
  }

  const currentRunningEntry = job.timeEntries?.find(
    (entry) => entry.userId === currentUserId && entry.isRunning,
  );
  const anyRunningEntry = job.timeEntries?.find((entry) => entry.isRunning);
  const running = Boolean(currentRunningEntry || (canEditSchedule && anyRunningEntry));
  const address = encodeURIComponent(job.serviceAddress);
  const repeatInterval = job.repeatIntervalWeeks || 1;
  const repeatUnit = repeatInterval === 1 ? "week" : "weeks";
  const employees = assignedEmployees(job);

  function openNavigator() {
    present({
      cssClass: "metora-action-sheet",
      header: "Open in Navigator",
      buttons: [
        { text: "Apple Maps", handler: () => window.open(`http://maps.apple.com/?daddr=${address}`, "_blank") },
        { text: "Google Maps", handler: () => window.open(`https://www.google.com/maps/dir/?api=1&destination=${address}`, "_blank") },
        { text: "Cancel", role: "cancel" },
      ],
    });
  }

  function callEmployee(phoneNumber) {
    window.location.href = `tel:${phoneNumber}`;
  }

  function callPhone(phoneNumber) {
    window.location.href = `tel:${phoneNumber}`;
  }

  function openScheduleEditor() {
    setIsTimeAllocationEnabled(false);
    setIsPayAllocationEnabled(false);
    setIsEditingSchedule(true);
  }

  function closeScheduleEditor() {
    setIsTimeAllocationEnabled(false);
    setIsPayAllocationEnabled(false);
    setIsEditingSchedule(false);
  }

  function updateScheduledStart(scheduledStart) {
    setScheduleForm((current) => ({
      ...current,
      scheduledStart,
    }));
  }

  function updateAssignedUsers(assignedUserIds) {
    const nextStandardUsers = users.filter(
      (user) => assignedUserIds.includes(user.id) && user.role?.name === "standard_user" && !user.isOwner,
    );
    const nextAllocations = Object.fromEntries(
      nextStandardUsers.map((user) => [
        user.id,
        employeeAllocations[user.id] || durationParts(effectivePayrollSeconds(job, user.id)),
      ]),
    );
    const nextPayRates = Object.fromEntries(
      nextStandardUsers.map((user) => [
        user.id,
        employeePayRates[user.id] ?? String(effectiveHourlyRate(job, user)),
      ]),
    );

    setScheduleForm((current) => ({ ...current, assignedUserIds }));
    setEmployeeAllocations(nextAllocations);
    setEmployeePayRates(nextPayRates);

    if (nextStandardUsers.length > 0 && standardAssignedUsers.length === 0) {
      setSameAllocation(nextAllocations[nextStandardUsers[0].id]);
      setSamePayRate(nextPayRates[nextStandardUsers[0].id]);
    }
  }

  function updateEmployeeAllocation(userId, field, value) {
    setEmployeeAllocations((current) => ({
      ...current,
      [userId]: {
        ...current[userId],
        [field]: value,
      },
    }));
  }

  async function saveSchedule() {
    if (!scheduledDurationMinutes || isSavingSchedule) {
      return;
    }

    const scheduledEnd = calculatedScheduledEnd(
      scheduleForm.scheduledStart,
      scheduledDurationMinutes,
      scheduleForm.assignedUserIds.length,
    );

    const allocations = isTimeAllocationEnabled
      ? standardAssignedUsers.map((user) => ({
        userId: user.id,
        durationSeconds: allocationMode === "same"
          ? durationSeconds(sameAllocation)
          : durationSeconds(employeeAllocations[user.id]),
      }))
      : undefined;
    const payAllocations = isPayAllocationEnabled
      ? standardAssignedUsers.map((user) => {
        const enteredRate = Number(payAllocationMode === "same" ? samePayRate : employeePayRates[user.id]);
        const defaultRate = Number(user.hourlyRate || 0);

        return {
          userId: user.id,
          hourlyRateOverride: enteredRate === defaultRate ? null : enteredRate,
        };
      })
      : undefined;

    try {
      setIsSavingSchedule(true);
      await onUpdateSchedule(
        job.id,
        {
          scheduledStart: new Date(scheduleForm.scheduledStart),
          scheduledEnd,
          estimatedDuration: scheduledDurationMinutes,
          status: job.status,
          assignedUserIds: scheduleForm.assignedUserIds,
        },
        allocations,
        payAllocations,
      );
      closeScheduleEditor();
    } catch {
      // The parent displays the API error and keeps this modal open.
    } finally {
      setIsSavingSchedule(false);
    }
  }

  return (
    <>
      <IonModal isOpen={isOpen} onDidDismiss={onClose} initialBreakpoint={0.8} breakpoints={[0, 0.8, 1]}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>Job Details</IonTitle>
            {canEditSchedule && (
              <IonButtons slot="end">
                <IonButton onClick={openScheduleEditor} aria-label="Edit scheduled job">
                  <IonIcon icon={createOutline} />
                </IonButton>
              </IonButtons>
            )}
          </IonToolbar>
        </IonHeader>
        <IonContent className="sheet-content">
          <section className="expanded-job">
            <StatusBadge status={job.status} />
            <div className="expanded-job-client-row">
              <h2>{jobLabel(job) || "Client"}</h2>
              <div className="employee-contact client-heading-contact">
                <span className="selectable-text">{job.client?.phone || "No phone number"}</span>
                {job.client?.phone && (
                  <IonButton
                    fill="clear"
                    size="small"
                    onClick={() => callPhone(job.client.phone)}
                    aria-label={`Call ${job.client?.name || "client"}`}
                  >
                    <IonIcon icon={callOutline} />
                  </IonButton>
                )}
              </div>
            </div>
            <p>{job.serviceAddress}</p>
            <p>
              {formatTime(job.scheduledStart)} - {formatTime(job.scheduledEnd)}
            </p>
            {job.repeatGroupId && (
              <p>
                Repeats every {repeatInterval} {repeatUnit}
                {job.repeatLastDate ? ` until ${formatDate(job.repeatLastDate)}` : ""}
              </p>
            )}
            <div className="expanded-actions">
              <TimerDisplay
                entries={job.timeEntries}
                fallbackSeconds={(job.estimatedDuration || 0) * 60}
              />
              <PlayPauseButton running={running} onClick={() => onToggleTimer(job, running)} />
            </div>
            <div className="detail-grid">
              <div>
                <span>Estimated Duration</span>
                <strong>{formatDuration((job.estimatedDuration || 0) * 60)}</strong>
              </div>
              <div>
                <span>Assigned Employees</span>
                <strong>{assignedEmployeeCount(job)}</strong>
              </div>
            </div>
            <h3>Assigned Employees</h3>
            <div className="employee-list">
              {employees.map((assignment) => {
                const isCurrentUser = assignment.user.id === currentUserId;
                const phoneNumber = assignment.user.employeeNumber;

                return (
                  <div key={assignment.id}>
                    <div>
                      <span>{assignment.user.name}</span>
                      {isCurrentUser && <small>You</small>}
                    </div>

                    <div className="employee-contact">
                      <span className="selectable-text">{phoneNumber || "No phone number"}</span>
                      {phoneNumber && (
                        <IonButton
                          fill="clear"
                          size="small"
                          onClick={() => callEmployee(phoneNumber)}
                          aria-label={`Call ${assignment.user.name}`}
                        >
                          <IonIcon icon={callOutline} />
                        </IonButton>
                      )}
                    </div>
                  </div>
                );
              })}
              {employees.length === 0 && <p>No assigned employees.</p>}
            </div>
            <h3>Client Notes</h3>
            <p className="notes">{job.client?.notes || "No client notes."}</p>
            <h3>Service Notes</h3>
            <p className="notes">{job.serviceNotes || "No service notes."}</p>
            <IonButton expand="block" className="primary-action" onClick={openNavigator}>
              <IonIcon icon={navigateOutline} slot="start" />
              Open in Navigator
            </IonButton>
            {canEditSchedule && job.status !== "Cancelled" && (
              <IonButton expand="block" color="danger" onClick={() => setIsCancelOpen(true)}>
                Cancel Scheduled Job
              </IonButton>
            )}
          </section>

          <IonAlert
            cssClass="metora-alert"
            isOpen={isCancelOpen}
            header="Cancel Scheduled Job"
            message="Cancel this scheduled visit only? The repeated job will continue."
            buttons={[
              { text: "Keep", role: "cancel" },
              {
                text: "Cancel Job",
                role: "destructive",
                handler: () => onCancelSchedule(job.id),
              },
            ]}
            onDidDismiss={() => setIsCancelOpen(false)}
          />
        </IonContent>
      </IonModal>

      <IonModal
        className="schedule-edit-modal"
        isOpen={isEditingSchedule}
        onDidDismiss={closeScheduleEditor}
      >
        <IonHeader>
          <IonToolbar>
            <IonTitle>Edit Scheduled Job</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={closeScheduleEditor}>Close</IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <main className="page-pad">
            <section className="panel form-grid">
              <SearchSelect
                label="Assigned Employees"
                multiple
                placeholder="Select employees"
                options={users.map((user) => ({
                  value: user.id,
                  label: user.id === currentUserId ? "Myself" : user.name,
                }))}
                value={scheduleForm.assignedUserIds}
                onChange={updateAssignedUsers}
              />

              <IonInput
                className="metora-input"
                label="Scheduled Start"
                labelPlacement="stacked"
                type="datetime-local"
                value={scheduleForm.scheduledStart}
                onIonInput={(event) => updateScheduledStart(event.detail.value ?? "")}
              />

              <div className="duration-inputs">
                <IonInput
                  className="metora-input"
                  label="Hours"
                  labelPlacement="stacked"
                  type="number"
                  inputmode="numeric"
                  min="0"
                  placeholder="0"
                  value={scheduleForm.estimatedDurationHours}
                  onIonInput={(event) =>
                    setScheduleForm({
                      ...scheduleForm,
                      estimatedDurationHours: event.detail.value ?? "",
                    })
                  }
                />

                <IonInput
                  className="metora-input"
                  label="Minutes"
                  labelPlacement="stacked"
                  type="number"
                  inputmode="numeric"
                  min="0"
                  max="59"
                  placeholder="00"
                  value={scheduleForm.estimatedDurationMinutesPart}
                  onIonInput={(event) =>
                    setScheduleForm({
                      ...scheduleForm,
                      estimatedDurationMinutesPart: event.detail.value ?? "",
                    })
                  }
                  onIonBlur={() =>
                    setScheduleForm((current) => ({
                      ...current,
                      estimatedDurationMinutesPart: normalizeMinutePart(current.estimatedDurationMinutesPart),
                    }))
                  }
                />
              </div>
              <IonInput
                className="metora-input"
                label="Estimated Duration in Minutes"
                labelPlacement="stacked"
                type="number"
                readonly
                value={scheduledDurationMinutes || ""}
                placeholder="Calculated from estimated hours"
              />

              <label className="checkbox-row">
                <IonCheckbox
                  checked={isTimeAllocationEnabled}
                  onIonChange={(event) => setIsTimeAllocationEnabled(event.detail.checked)}
                />
                <span>Edit Time Allocation</span>
              </label>

              {isTimeAllocationEnabled && (
                <section className="time-allocation">
                  <span className="time-allocation-label">Time Allocation</span>
                <IonSegment
                  value={allocationMode}
                  onIonChange={(event) => setAllocationMode(event.detail.value || "same")}
                >
                  <IonSegmentButton value="same">
                    <IonLabel>Same for all</IonLabel>
                  </IonSegmentButton>
                  <IonSegmentButton value="employee">
                    <IonLabel>Per employee</IonLabel>
                  </IonSegmentButton>
                </IonSegment>

                {standardAssignedUsers.length === 0 ? (
                  <p className="time-allocation-empty">Assign a standard employee to edit payroll time.</p>
                ) : allocationMode === "same" ? (
                  <div className="time-allocation-body">
                    <strong>Same working time for all employees</strong>
                    <p>This payroll time will apply equally to every assigned standard employee.</p>
                    <div className="duration-inputs">
                      <IonInput
                        className="metora-input"
                        label="Hours"
                        labelPlacement="stacked"
                        type="number"
                        min="0"
                        value={sameAllocation.hours}
                        onIonInput={(event) =>
                          setSameAllocation((current) => ({
                            ...current,
                            hours: event.detail.value ?? "0",
                          }))
                        }
                      />
                      <IonInput
                        className="metora-input"
                        label="Minutes"
                        labelPlacement="stacked"
                        type="number"
                        min="0"
                        max="59"
                        value={sameAllocation.minutes}
                        onIonInput={(event) =>
                          setSameAllocation((current) => ({
                            ...current,
                            minutes: event.detail.value ?? "0",
                          }))
                        }
                        onIonBlur={() =>
                          setSameAllocation((current) => durationParts(durationSeconds(current)))
                        }
                      />
                    </div>
                  </div>
                ) : (
                  <div className="time-allocation-body">
                    <strong>Individual working time per employee</strong>
                    <p>Set the payroll time separately for each assigned standard employee.</p>
                    <div className="employee-time-list">
                      {standardAssignedUsers.map((user) => (
                        <div className="employee-time-row" key={user.id}>
                          <span>{user.name}</span>
                          <IonInput
                            className="metora-input compact-input"
                            label="Hours"
                            labelPlacement="stacked"
                            type="number"
                            min="0"
                            value={employeeAllocations[user.id]?.hours ?? "0"}
                            onIonInput={(event) =>
                              updateEmployeeAllocation(user.id, "hours", event.detail.value ?? "0")
                            }
                          />
                          <IonInput
                            className="metora-input compact-input"
                            label="Minutes"
                            labelPlacement="stacked"
                            type="number"
                            min="0"
                            max="59"
                            value={employeeAllocations[user.id]?.minutes ?? "0"}
                            onIonInput={(event) =>
                              updateEmployeeAllocation(user.id, "minutes", event.detail.value ?? "0")
                            }
                            onIonBlur={() =>
                              setEmployeeAllocations((current) => ({
                                ...current,
                                [user.id]: durationParts(durationSeconds(current[user.id])),
                              }))
                            }
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                </section>
              )}

              <label className="checkbox-row">
                <IonCheckbox
                  checked={isPayAllocationEnabled}
                  onIonChange={(event) => setIsPayAllocationEnabled(event.detail.checked)}
                />
                <span>Edit Pay Allocation</span>
              </label>

              {isPayAllocationEnabled && (
                <section className="time-allocation">
                  <span className="time-allocation-label">Pay Allocation</span>
                <IonSegment
                  value={payAllocationMode}
                  onIonChange={(event) => setPayAllocationMode(event.detail.value || "same")}
                >
                  <IonSegmentButton value="same">
                    <IonLabel>Same for all</IonLabel>
                  </IonSegmentButton>
                  <IonSegmentButton value="employee">
                    <IonLabel>Per employee</IonLabel>
                  </IonSegmentButton>
                </IonSegment>

                {standardAssignedUsers.length === 0 ? (
                  <p className="time-allocation-empty">Assign a standard employee to edit payroll rates.</p>
                ) : payAllocationMode === "same" ? (
                  <div className="time-allocation-body">
                    <strong>Same hourly rate for all employees</strong>
                    <p>This hourly rate will apply to every assigned standard employee for this scheduled job only.</p>
                    <IonInput
                      className="metora-input"
                      label="Hourly Rate"
                      labelPlacement="stacked"
                      type="number"
                      inputmode="decimal"
                      min="0"
                      step="0.01"
                      value={samePayRate}
                      onIonInput={(event) => setSamePayRate(event.detail.value ?? "0")}
                    />
                  </div>
                ) : (
                  <div className="time-allocation-body">
                    <strong>Individual hourly rate per employee</strong>
                    <p>Override the default hourly rate separately for each assigned standard employee.</p>
                    <div className="employee-time-list">
                      {standardAssignedUsers.map((user) => (
                        <div className="employee-pay-row" key={user.id}>
                          <span>{user.name}</span>
                          <IonInput
                            className="metora-input compact-input"
                            label="Hourly Rate"
                            labelPlacement="stacked"
                            type="number"
                            inputmode="decimal"
                            min="0"
                            step="0.01"
                            value={employeePayRates[user.id] ?? String(user.hourlyRate || 0)}
                            onIonInput={(event) =>
                              setEmployeePayRates((current) => ({
                                ...current,
                                [user.id]: event.detail.value ?? "0",
                              }))
                            }
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                </section>
              )}

              <IonButton
                expand="block"
                onClick={saveSchedule}
                disabled={isSavingSchedule || !scheduleForm.scheduledStart || !scheduledDurationMinutes}
              >
                {isSavingSchedule && <IonSpinner name="crescent" className="button-spinner" />}
                Save Scheduled Job
              </IonButton>

              <IonButton
                expand="block"
                fill="outline"
                color="danger"
                disabled={isSavingSchedule || isDeletingSchedule}
                onClick={() => setIsDeleteOpen(true)}
              >
                {isDeletingSchedule ? "Deleting..." : "Delete Scheduled Job"}
              </IonButton>
            </section>
          </main>
        </IonContent>
      </IonModal>

      <IonAlert
        cssClass="metora-alert"
        isOpen={isDeleteOpen}
        backdropDismiss={!isDeletingSchedule}
        header="Delete Scheduled Job"
        message="Remove this scheduled visit? It will be hidden from the schedule and excluded from future invoices and pay runs. Existing financial history will be preserved."
        buttons={[
          { text: "Keep", role: "cancel", htmlAttributes: { disabled: isDeletingSchedule } },
          {
            text: isDeletingSchedule ? "Deleting..." : "Delete",
            role: "destructive",
            htmlAttributes: { disabled: isDeletingSchedule },
            handler: () => {
              void onDeleteSchedule(job.id);

              return false;
            },
          },
        ]}
        onDidDismiss={() => setIsDeleteOpen(false)}
      />
    </>
  );
}
